import { useState, useRef, useEffect, useCallback } from 'react';
import { X, TerminalSquare, CornerDownLeft } from 'lucide-react';
import api from '../api/client';

/**
 * Ported from primus-admin-main's AdminConsoleModal.jsx.
 *
 * Flow: POST /internal/pcs/{cafeId}/command {command:"admin_console", params:{command_line,...}} →
 * the kiosk runs it headless as its separate admin Windows account (NOT SYSTEM) →
 * poll /internal/pcs/{cafeId}/history/{pcId} for that command id's ACK and render the
 * captured output. The per-PC admin_console_enabled toggle is enforced server-side
 * (a disabled PC returns 403 on send) -- use the deck's Enable console action first.
 */
const POLL_MS = 1500;
const POLL_TIMEOUT_MS = 90_000;

export default function AdminConsoleModal({ pc, cafeId, onClose }) {
    const [cmd, setCmd] = useState('');
    // Mutually exclusive: exactly one shell runs the command.
    const [shell, setShell] = useState('cmd');
    const [entries, setEntries] = useState([]); // { id, command, shell, state, output }
    const [runningCount, setRunningCount] = useState(0);
    const running = runningCount > 0;
    const outRef = useRef(null);
    // commandId -> intervalId, keyed so multiple in-flight commands can poll concurrently.
    const pollsRef = useRef({});

    useEffect(() => () => {
        Object.values(pollsRef.current).forEach(clearInterval);
    }, []);

    useEffect(() => {
        if (outRef.current) outRef.current.scrollTop = outRef.current.scrollHeight;
    }, [entries]);

    const pollForResult = useCallback((commandId) => {
        const started = Date.now();
        pollsRef.current[commandId] = setInterval(async () => {
            try {
                const r = await api.get(`/internal/pcs/${cafeId}/history/${pc.id}`);
                const row = (r.data || []).find((c) => c.id === commandId);
                if (row && ['SUCCEEDED', 'FAILED', 'EXPIRED'].includes(row.state)) {
                    clearInterval(pollsRef.current[commandId]);
                    delete pollsRef.current[commandId];
                    const output = row.result?.output
                        ?? (row.state === 'EXPIRED' ? '(command expired before the kiosk picked it up)' : '(no output)');
                    setEntries((prev) => prev.map((e) => e.id === commandId ? { ...e, state: row.state, output } : e));
                    setRunningCount((n) => Math.max(0, n - 1));
                }
            } catch {
                // transient — keep polling until timeout
            }
            if (Date.now() - started > POLL_TIMEOUT_MS && pollsRef.current[commandId]) {
                clearInterval(pollsRef.current[commandId]);
                delete pollsRef.current[commandId];
                setEntries((prev) => prev.map((e) => e.id === commandId && e.state === 'RUNNING'
                    ? { ...e, state: 'TIMEOUT', output: '(timed out waiting for the kiosk to report back)' } : e));
                setRunningCount((n) => Math.max(0, n - 1));
            }
        }, POLL_MS);
    }, [cafeId, pc.id]);

    const dispatchOne = async (line, shell) => {
        try {
            const payload = {
                pc_id: pc.id,
                command: 'admin_console',
                params: JSON.stringify({ command_line: line, run_in_cmd: true, shell }),
            };
            const r = await api.post(`/internal/pcs/${cafeId}/command`, payload);
            const commandId = r.data?.id;
            if (commandId) {
                setEntries((prev) => [...prev, { id: commandId, command: line, shell, state: 'RUNNING', output: '' }]);
                pollForResult(commandId);
            } else {
                setEntries((prev) => [...prev, { id: `err-${Date.now()}-${shell}`, command: line, shell, state: 'FAILED', output: '(no command id returned)' }]);
                setRunningCount((n) => Math.max(0, n - 1));
            }
        } catch (e) {
            const detail = e.response?.data?.detail || e.message;
            setEntries((prev) => [...prev, { id: `err-${Date.now()}-${shell}`, command: line, shell, state: 'FAILED', output: detail }]);
            setRunningCount((n) => Math.max(0, n - 1));
        }
    };

    const run = async () => {
        const line = cmd.trim();
        if (!line || running) return;
        setCmd('');
        setRunningCount((n) => n + 1);
        dispatchOne(line, shell);
    };

    const onKeyDown = (e) => {
        if (e.key === 'Enter') { e.preventDefault(); run(); }
        if (e.key === 'Escape') onClose();
    };

    return (
        <div style={overlay} onClick={onClose}>
            <div style={panel} onClick={(e) => e.stopPropagation()}>
                <div style={head}>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8, color: '#fff', fontWeight: 700 }}>
                        <TerminalSquare size={18} /> Admin console — {pc.name}
                    </span>
                    <button onClick={onClose} title="Close" style={closeBtn}><X size={18} /></button>
                </div>

                <p style={{ margin: '0 0 12px', fontSize: 12, color: '#9CA3AF', lineHeight: 1.5 }}>
                    Commands run on this kiosk as its <span style={{ color: '#fff' }}>admin Windows account</span> (not SYSTEM),
                    headless — nothing appears on the customer&apos;s screen. Every run is audited.
                </p>

                <div ref={outRef} style={term}>
                    {entries.length === 0 && (
                        <div style={{ color: '#6b7280' }}>No commands run yet. Try <code>whoami</code> or <code>hostname</code>.</div>
                    )}
                    {entries.map((e) => (
                        <div key={e.id} style={{ marginBottom: 10 }}>
                            <div style={{ color: '#7dd3fc' }}>
                                &gt; {e.command} <span style={{ color: '#9CA3AF', fontSize: 11 }}>[{e.shell}]</span>
                            </div>
                            {e.state === 'RUNNING'
                                ? <div style={{ color: '#fcd34d' }}>running…</div>
                                : <pre style={pre}>{e.output}</pre>}
                            {e.state && e.state !== 'RUNNING' && (
                                <div style={{ color: e.state === 'SUCCEEDED' ? '#4ade80' : '#f87171', fontSize: 11 }}>[{e.state}]</div>
                            )}
                        </div>
                    ))}
                </div>

                <div style={shellRow}>
                    <label style={shellLabel}>
                        <input
                            type="radio"
                            name="shell"
                            checked={shell === 'cmd'}
                            onChange={() => setShell('cmd')}
                        /> cmd
                    </label>
                    <label style={shellLabel}>
                        <input
                            type="radio"
                            name="shell"
                            checked={shell === 'powershell'}
                            onChange={() => setShell('powershell')}
                        /> powershell
                    </label>
                </div>

                <div style={inputRow}>
                    <span style={{ color: '#7dd3fc', fontFamily: 'monospace' }}>&gt;</span>
                    <input
                        type="text"
                        value={cmd}
                        onChange={(e) => setCmd(e.target.value)}
                        onKeyDown={onKeyDown}
                        autoFocus
                        placeholder={`Enter a command (runs via ${shell === 'powershell' ? 'powershell.exe' : 'cmd.exe'})…`}
                        style={input}
                    />
                    <button onClick={run} disabled={running || !cmd.trim()} style={runBtn} title="Run (Enter)">
                        <CornerDownLeft size={16} /> Run
                    </button>
                </div>
            </div>
        </div>
    );
}

const overlay = { position: 'fixed', inset: 0, zIndex: 9999, background: 'rgba(0,0,0,0.7)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 };
const panel = { background: '#111827', border: '1px solid rgba(255,255,255,0.15)', borderRadius: 16, padding: 22, width: '100%', maxWidth: 760, color: '#fff' };
const head = { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 };
const closeBtn = { background: 'transparent', border: 'none', color: '#aeb4c0', cursor: 'pointer', display: 'inline-flex' };
const term = { background: '#0b0f17', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 10, padding: 14, height: 360, overflowY: 'auto', fontFamily: 'monospace', fontSize: 13, lineHeight: 1.5 };
const pre = { margin: '2px 0 0', whiteSpace: 'pre-wrap', wordBreak: 'break-word', color: '#e5e7eb', fontFamily: 'monospace', fontSize: 13 };
const shellRow = { display: 'flex', alignItems: 'center', gap: 16, marginTop: 12 };
const shellLabel = { display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, color: '#d1d5db', cursor: 'pointer' };
const inputRow = { display: 'flex', alignItems: 'center', gap: 8, marginTop: 8 };
const input = { flex: 1, height: 42, padding: '0 12px', borderRadius: 10, background: 'rgba(255,255,255,0.07)', border: '1px solid rgba(255,255,255,0.15)', color: '#fff', fontSize: 14, fontFamily: 'monospace', outline: 'none' };
const runBtn = { display: 'inline-flex', alignItems: 'center', gap: 6, padding: '0 16px', height: 42, borderRadius: 10, border: 'none', background: '#7c3aed', color: '#fff', cursor: 'pointer', fontSize: 14, fontWeight: 600 };
